import React from 'react';
import { AlertTriangle, PlusCircle } from 'lucide-react';
import type { ContextUsage } from '../hooks/usePlanLimits';

/**
 * Warn from 80% on. A hard stop that arrives with no warning is the main cost
 * of refusing rather than silently dropping old turns, and a bar is the whole
 * fix for it.
 */
const WARN_AT = 0.8;

export function ContextUsageBar({
  usage,
  isChinese,
}: {
  usage: ContextUsage | null;
  isChinese: boolean;
}) {
  if (!usage || usage.limit <= 0) return null;

  const ratio = Math.min(1, usage.used / usage.limit);
  if (ratio < WARN_AT) return null;

  const full = usage.used >= usage.limit;
  const tone = full
    ? 'bg-red-500'
    : 'bg-amber-500';

  return (
    <div className="px-4 pb-1.5">
      <div className="flex items-center justify-between text-[11px] text-gray-500 dark:text-zinc-400">
        <span>{isChinese ? '对话长度' : 'Conversation length'}</span>
        <span className={full ? 'text-red-500' : 'text-amber-600 dark:text-amber-400'}>
          {usage.used.toLocaleString()} / {usage.limit.toLocaleString()}
        </span>
      </div>
      <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-zinc-700">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${ratio * 100}%` }} />
      </div>
    </div>
  );
}

/**
 * Shown in place of the composer once the conversation is full. The button only
 * starts a new conversation — carrying context forward is a separate decision
 * about what to bring and what it costs.
 */
export function ContextFullNotice({
  onNewConversation,
  isChinese,
}: {
  onNewConversation?: () => void;
  isChinese: boolean;
}) {
  return (
    <div className="mx-4 mb-3 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm dark:border-red-500/30 dark:bg-red-500/10">
      <AlertTriangle size={16} className="shrink-0 text-red-500" />
      <div className="flex-1 text-red-700 dark:text-red-300">
        {isChinese
          ? '本对话已达长度上限，请新建对话继续。'
          : 'This conversation has reached its maximum length. Start a new one to continue.'}
      </div>
      {onNewConversation && (
        <button
          type="button"
          onClick={onNewConversation}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-red-500 px-3 py-1.5 text-xs text-white hover:bg-red-600"
        >
          <PlusCircle size={14} />
          {isChinese ? '新建对话' : 'New conversation'}
        </button>
      )}
    </div>
  );
}
